import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { accessDeniedDescriptions } from 'src/app/core/_helpers/accessDeniedDescriptions';
import { ApiResponse, GenericItemApiResponse, GenericListApiResponse } from 'src/app/core/_interface/apiResponse';
import { CompanyAdminSettings, CompanySettings, ICompany } from 'src/app/core/_interface/company';
import { EffectivePermissions, Permissions, UserPermissions } from 'src/app/core/_interface/permissions';
import { Employee, User } from 'src/app/core/_interface/user';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {



  saved = new Subject<ICompany>();
  companySettingsSaved = new Subject<CompanySettings>();

  constructor(private http: HttpClient) {

  }

  searchCompanyByTaxNo(taxNo: string) {
    const apiAddress = environment.apiBase + 'company/getByTaxNo/?taxNo=' + taxNo;

    const response = this.http.get<GenericItemApiResponse<ICompany>>(apiAddress).pipe(
      map((data) => {
        if (data.success) {
          return data.item;
        }
        throw data;
      })
    );
    return response;
  }

  getCompany(companyID: number) {
    const apiAddress = environment.apiBase + 'company/getByID/?id=' + companyID;

    const response = this.http.get<GenericItemApiResponse<ICompany>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        return null;
      })
    );

    return response;
  }

  /** PublicID (GUID) organizacije kojoj firma pripada — koristi ga loyalty aplikacija (query string / "organization" header). */
  getOrganizationPublicID(companyID: number): Observable<string | null> {
    const apiAddress = environment.apiBase + 'company/getOrganizationPublicID/?companyID=' + companyID;

    return this.http.get<string | null>(apiAddress).pipe(
      map(id => id || null)
    );
  }

  getCompaniesByName(name: string): Observable<ICompany[] | null> {
    const apiAddress = environment.apiBase + 'company/getByName/?name=' + name;

    const response = this.http.get<GenericListApiResponse<ICompany>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.items;
        }
        return null;
      })
    );

    return response;
  }

  getCompaniesByOwnerID(id: number): Observable<ICompany[] | null> {
    const apiAddress = environment.apiBase + 'company/getByOwnerID/?userid=' + id;

    const response = this.http.get<GenericListApiResponse<ICompany>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.items;
        }
        return null;
      })
    );
    return response;
  }

  getNewest(): Observable<ICompany[] | null> {
    const apiAddress = environment.apiBase + 'company/getNewest/';

    const response = this.http.get<GenericListApiResponse<ICompany>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.items;
        }
        return null;
      })
    );
    return response;
  }

  getUserCompanies(): Observable<ICompany[]> {
    const url = environment.apiBase + `company/GetUserCompanies`;
    return this.http.get<GenericListApiResponse<ICompany>>(url)
      .pipe(
        map(response => {
          if (response.success) {
            return response.items;
          }
          return [];
        })
      );
  }

  save(company: ICompany): Observable<ICompany | null> {
    const apiAddress = environment.apiBase + 'company/save';

    const response = this.http.post<GenericItemApiResponse<ICompany>>(apiAddress, company).pipe(
      map((data) => {
        if (data.success) {
          return data.item;
        }
        throw data;
      }),
      tap(data => {
        if (data) {
          this.saved.next(data)
        }
      }));

    return response;
  }


  saveBranch(branch: ICompany): Observable<ICompany | null> {
    const apiAddress = environment.apiBase + 'company/saveFacility';

    const response = this.http.post<GenericItemApiResponse<ICompany>>(apiAddress, branch).pipe(
      map((data) => {
        if (data.success) {
          return data.item;
        }
        throw data;
      }),
      tap(data => {
        if (data) {
          this.saved.next(data)
        }
      }));

    return response;
  }

  saveEmployee(companyID: number, employee: Employee, permissions: Permissions[]): Observable<number> {
    const apiAddress = environment.apiBase + 'company/saveEmployee';
    const postData = {
      companyID: companyID,
      userID: employee.userID,
      firstName: employee.firstName,
      lastName: employee.lastName,
      eMail: employee.eMail,
      referenceID: employee.referenceId,
      sendNotifications: employee.sendNotifications,
      permissions: permissions,
      pin: employee.pin,
      allowMultipleSessions: employee.allowMultipleSessions,
      // Izostavljeno polje znači „ne diraj rolu" — zato se ne šalje kad ga pozivalac nije postavio.
      roleID: employee.roleID
    };



    const response = this.http.post<GenericItemApiResponse<number>>(apiAddress, postData, {
      headers: { accessDeniedDescription: accessDeniedDescriptions.saveEmployee }
    }).pipe(
      map((data) => {
        if (data.success) {
          return data.item;
        }
        return 0;
      }
      ));

    return response;
  }

  removeEmployee(companyID: number, userID: number): Observable<boolean> {
    const apiAddress = environment.apiBase + 'company/RemoveEmployee';
    const postData = {
      companyID: companyID,
      userID: userID
    };

    const response = this.http.post<ApiResponse>(apiAddress, postData).pipe(
      map((data) => data.success)
    );

    return response;
  }


  /**
   * Upload slike firme.
   *
   * ⚠ `'eatInImage'` i `'pickupImage'` su OBSOLETE — upload je uklonjen iz admin portala,
   * te dve slike se od sada menjaju org CSS tokenima `--app-icon-eatin` / `--app-icon-pickup`
   * (v. WebKioskApp/docs/customization.md). Tipovi ostaju u potpisu jer server i dalje prima
   * i isporučuje te slike zbog starih verzija kioska; brišu se kad te verzije izađu iz upotrebe.
   */
  uploadImage(companyID: number, type: 'logo' | 'background' | 'QRCodeLogo' | 'userAppIcon' | 'printImage' | 'eatInImage' | 'pickupImage', file: File): Observable<boolean> {
    const url = environment.apiBase + 'company/uploadImage';

    let formData: FormData = new FormData();
    formData.append('uploadFile', file, file.name);
    formData.append('companyID', companyID.toString());
    formData.append('type', type);

    return this.http.post<ApiResponse>(url, formData)
      .pipe(
        map(response => {
          return response.success;
        })
      );
  }


  getEmployees(companyID: number) {
    if (!companyID) {
      return of([]);
    }

    const apiAddress = environment.apiBase + 'company/GetEmployees/?companyid=' + companyID;

    const response = this.http.get<GenericListApiResponse<Employee>>(apiAddress, {
      headers: { accessDeniedDescription: accessDeniedDescriptions.getEmployees }
    }).pipe(
      map((data) => {
        if (data.success) {
          return data.items.map(emp => {
            var employee = Object.assign(new Employee(), emp);
            return employee;
          });
        }
        throw data;
      })
    );
    return response;

  }



  getEmployee(companyID: number, uniqueID: string): Observable<User | null> {
    if (!companyID) {
      return of(null);
    }

    const apiAddress = environment.apiBase + 'company/GetEmployee';
    var data = {
      companyID: companyID,
      uniqueID: uniqueID
    };

    const response = this.http.post<GenericItemApiResponse<User>>(apiAddress, data).pipe(
      map((data) => {
        if (data.success) {
          return data.item;
        }
        throw data;
      })
    );
    return response;
  }


  getSettings(id: number): Observable<CompanySettings | null> {
    const apiAddress = environment.apiBase + 'company/getSettings/?companyID=' + id;

    const response = this.http.get<GenericItemApiResponse<CompanySettings>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        return null;
      })
    );

    return response;
  }

  getAdminSettings(id: number) {
    const apiAddress = environment.apiBase + 'company/getAdminSettings/' + id;

    const response = this.http.get<GenericItemApiResponse<CompanyAdminSettings>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        throw data;
      })
    );

    return response;
  }

  saveSettings(companyID: number, settings: CompanySettings): Observable<boolean> {
    const apiAddress = environment.apiBase + 'company/saveSettings/?companyID=' + companyID;

    const response = this.http.post<ApiResponse>(apiAddress, settings).pipe(
      tap(
        result => {
          if (result.success) {
            this.companySettingsSaved.next(settings);
          }
        }
      ),
      map((data) => {
        return data.success;
      })
    );
    return response;
  }

  saveAdminSettings(companyID: number, settings: CompanyAdminSettings): Observable<boolean> {
    const apiAddress = environment.apiBase + 'company/saveAdminSettings/?companyID=' + companyID;

    const response = this.http.post<ApiResponse>(apiAddress, settings).pipe(
      map((data) => {
        if (data.success)
          return data.success;

        throw data;

      })
    );
    return response;
  }

  /**
   * Prava prijavljenog korisnika nad lokalom I nad njegovom organizacijom, u jednom odgovoru.
   *
   * Zamenjuje <see cref="getPermissions"/> (koji vraća samo nivo lokala i kome je `userID` uvek bio
   * tekući korisnik). Nov endpoint je u novom stilu — goli DTO, greška samo kao HTTP status — pa
   * `catchError` na pozivaocu razlikuje „nema prava" od „odgovor nije stigao".
   */
  getMyPermissions(companyID: number): Observable<EffectivePermissions> {
    const apiAddress = environment.apiBase + `permissions/getMyPermissions/?companyID=${companyID}`;
    return this.http.get<EffectivePermissions>(apiAddress);
  }

  /** @deprecated Koristi `getMyPermissions` — ovaj poziv ne vidi prava na nivou organizacije. */
  getPermissions(companyID: number, userID: number): Observable<Permissions[] | null> {
    const apiAddress = environment.apiBase + `company/getPermissions/?companyID=${companyID}&userID=${userID}`;

    const response = this.http.get<GenericListApiResponse<Permissions>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.items?.map(i => Permissions[i] as any); //dobijamo string, konvertujemo u ENUM
        }
        return null;
      })
    );

    return response;
  }

  getAPIKey(companyID: number): Observable<string | null> {
    const apiAddress = environment.apiBase + `company/getAPIKey/?companyID=${companyID}`;

    const response = this.http.get<GenericItemApiResponse<string>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        return null;
      })
    );

    return response;
  }


  assignAPIKey(companyID: number): Observable<string | null> {
    const apiAddress = environment.apiBase + `company/assignAPIKey/?companyID=${companyID}`;

    const response = this.http.get<GenericItemApiResponse<string>>(apiAddress).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        return null;
      })
    );

    return response;
  }


  generateSignInUrl(companyID: number, userID: number) {
    const apiAddress = environment.apiBase + `company/GenerateSignInToken/?companyID=${companyID}&userID=${userID}`;

    const response = this.http.post<GenericItemApiResponse<string>>(apiAddress, null).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        return null;
      })
    );

    return response;
  }

  deleteDatabase(companyID: number, type: number) {
    const apiAddress = environment.apiBase + `company/DeleteDatabase/?companyID=${companyID}&type=${type}`;

    const response = this.http.post<ApiResponse>(apiAddress, null).pipe(
      map(data => {
        return data.success
      })
    );

    return response
  }


  logOutUser(companyID: number, userID: number) {
    const apiAddress = environment.apiBase + `company/LogOutUser/?companyID=${companyID}&userid=${userID}`;

    const response = this.http.post<ApiResponse>(apiAddress, null).pipe(
      map(data => {
        return data.success
      })
    );

    return response
  }


  setPublic(companyID: number, isPublic: boolean): Observable<boolean> {
    const apiAddress = environment.apiBase + 'company/setPublic/?companyID=' + companyID + '&isPublic=' + isPublic;

    const response = this.http.post<ApiResponse>(apiAddress, null).pipe(
      map((data) => {
        return data.success;
      })
    );
    return response;
  }


  setDeleted(companyID: number, deleted: boolean) {
    const apiAddress = environment.apiBase + 'company/setDeleted';
    var data = {
      companyID,
      deleted
    }

    const response = this.http.post<ApiResponse>(apiAddress, data).pipe(
      map((data) => {
        return data.success;
      })
    );
    return response;
  }


  createEmployeeAPIKey(companyID: number, userID: number) {
    const apiAddress = environment.apiBase + `company/CreateEmployeeApiKey`;
    const data = {
      userID: userID,
      companyID: companyID
    };

    const response = this.http.post<GenericItemApiResponse<string>>(apiAddress, data).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        throw new Error('Error generating API key');
      })
    );

    return response;
  }

  close(companyID: number) {
    const apiAddress = environment.apiBase + 'company/close/?companyID=' + companyID;
    return this.http.post<GenericItemApiResponse<ICompany>>(apiAddress, {}).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        throw data;
      })
    );
  }

  open(companyID: number) {
    const apiAddress = environment.apiBase + 'company/open/?companyID=' + companyID;
    return this.http.post<GenericItemApiResponse<ICompany>>(apiAddress, {}).pipe(
      map(data => {
        if (data.success) {
          return data.item;
        }
        throw data;
      })
    );
  }


}
